/**
 * PaymentHistoryPanel
 * -------------------
 * Renders the chronological "Payment History" rail at the bottom of the
 * Lead Detail Billing tab. Pulls from `/coach/clients/{user_id}/payment-history`
 * which merges revenue_events (successful charges + refunds) with
 * billing_alerts (failed payments) and scheduled_charges (pending 50/50).
 *
 * Owner spec 2026-02-17:
 *  - Green dot + amount for SUCCEEDED
 *  - Red dot + "Payment Failed" label for FAILED (with the decline reason)
 *  - Amber for pending scheduled charges
 *
 * Lightweight — no pagination yet; the endpoint already caps at 50 rows.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CreditCard, AlertCircle, Clock, RefreshCcw, Undo2, FileText, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (cents) =>
    cents == null ? "—" : `$${(Math.abs(cents) / 100).toFixed(2)}`;

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

function StatusBadge({ status }) {
    if (status === "succeeded") {
        return (
            <span
                data-testid="payment-status-succeeded"
                className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-emerald-300 border border-emerald-700/50 bg-emerald-900/20 px-2 py-0.5"
            >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                Paid
            </span>
        );
    }
    if (status === "refunded") {
        return (
            <span
                data-testid="payment-status-refunded"
                className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-blue-300 border border-blue-700/50 bg-blue-900/20 px-2 py-0.5"
            >
                <RefreshCcw className="w-2.5 h-2.5" />
                Refunded
            </span>
        );
    }
    if (status === "failed" || status === "failed_resolved") {
        return (
            <span
                data-testid="payment-status-failed"
                className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-red-300 border border-red-700/60 bg-red-900/30 px-2 py-0.5 font-bold"
            >
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                Payment Failed
            </span>
        );
    }
    if (status === "pending") {
        return (
            <span
                data-testid="payment-status-pending"
                className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-amber-300 border border-amber-700/60 bg-amber-900/20 px-2 py-0.5"
            >
                <Clock className="w-2.5 h-2.5" />
                Scheduled
            </span>
        );
    }
    return null;
}

function ReceiptActions({ item }) {
    const [sending, setSending] = useState(false);
    const downloadUrl = `${API}/coach/receipts/${item.id}/pdf`;

    const resend = async () => {
        setSending(true);
        try {
            const r = await axios.post(
                `${API}/coach/receipts/${item.id}/email`,
                {},
                { withCredentials: true },
            );
            toast.success("Receipt sent", { description: `Emailed to ${r.data?.sent_to}` });
        } catch (e) {
            toast.error("Couldn't send receipt", { description: e?.response?.data?.detail || "Unknown error" });
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`receipt-download-${item.id}`}
                className="text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 border border-zinc-700 text-zinc-300 hover:border-white hover:text-white transition-colors rounded-sm inline-flex items-center gap-1"
                title="Download / view PDF receipt"
            >
                <FileText className="w-3 h-3" />
                Receipt
            </a>
            <button
                type="button"
                data-testid={`receipt-resend-${item.id}`}
                onClick={resend}
                disabled={sending}
                className="text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 border border-zinc-700 text-zinc-300 hover:border-emerald-500 hover:text-emerald-300 transition-colors rounded-sm inline-flex items-center gap-1 disabled:opacity-50"
                title="Email this receipt to the parent again"
            >
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Resend
            </button>
        </>
    );
}

function Row({ item, onRefund, refundingId, canRefund, locallyRefunded }) {
    const isFailed = item.status === "failed" || item.status === "failed_resolved";
    const serverSaysRefund = item.status === "refunded";
    const isRefund = serverSaysRefund || locallyRefunded;
    const isPending = item.status === "pending";
    const refundable = canRefund
        && !!item.stripe_charge_id
        && item.status === "succeeded"
        && !locallyRefunded         // hide button immediately after a click
        && (item.signed_amount_cents || item.amount_cents) > 0;
    const isRefunding = refundingId === item.id;
    return (
        <li
            data-testid={`payment-history-row-${item.id}`}
            className={`flex items-start gap-3 py-3 border-b border-zinc-900 last:border-b-0 ${
                isFailed ? "bg-red-950/20" : ""
            }`}
        >
            <div className="pt-0.5">
                {isFailed ? (
                    <AlertCircle className="w-4 h-4 text-red-400" />
                ) : isPending ? (
                    <Clock className="w-4 h-4 text-amber-400" />
                ) : (
                    <CreditCard className={`w-4 h-4 ${isRefund ? "text-blue-400" : "text-emerald-400"}`} />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                    <div className="text-sm text-white font-medium truncate" data-testid="payment-history-description">
                        {item.description || "Charge"}
                    </div>
                    <div
                        className={`font-mono text-sm font-semibold whitespace-nowrap ${
                            isFailed ? "text-red-300" : isRefund ? "text-blue-300" : isPending ? "text-amber-200" : "text-emerald-300"
                        }`}
                        data-testid="payment-history-amount"
                    >
                        {isRefund ? "−" : ""}{fmtMoney(item.amount_cents)}
                    </div>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                    <div className="text-[11px] text-zinc-500" data-testid="payment-history-occurred-at">
                        {fmtDate(item.occurred_at)}
                    </div>
                    <div className="flex items-center gap-2">
                        <StatusBadge status={item.status} />
                        {item.has_receipt && item.status === "succeeded" && (
                            <ReceiptActions item={item} />
                        )}
                        {locallyRefunded && !serverSaysRefund && (
                            <span
                                data-testid={`payment-history-refund-pending-${item.id}`}
                                className="text-[9px] tracking-[0.18em] uppercase text-blue-300 border border-blue-700/60 bg-blue-900/30 px-1.5 py-0.5 font-bold rounded-sm"
                                title="Refund confirmed by Stripe — updating status..."
                            >
                                ↺ Refunded · syncing
                            </span>
                        )}
                        {refundable && (
                            <button
                                type="button"
                                data-testid={`payment-history-refund-${item.id}`}
                                onClick={() => onRefund(item)}
                                disabled={isRefunding}
                                className="text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 border border-brand-red text-brand-red hover:bg-brand-red hover:text-white transition-colors rounded-sm inline-flex items-center gap-1 disabled:opacity-50"
                                title="Refund this charge"
                            >
                                {isRefunding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}
                                Refund
                            </button>
                        )}
                    </div>
                </div>
                {isFailed && item.error && (
                    <div className="text-[11px] text-red-300/90 mt-1 font-mono" data-testid="payment-history-error">
                        {item.error}
                    </div>
                )}
            </div>
        </li>
    );
}

function RefundModal({ item, busy, onClose, onSubmit }) {
    const fullCents = item.amount_cents || 0;
    const [amountStr, setAmountStr] = useState((fullCents / 100).toFixed(2));
    const [note, setNote] = useState("");
    const [reason, setReason] = useState("requested_by_customer");

    const cents = Math.round(parseFloat(amountStr || "0") * 100);
    const amountValid = Number.isFinite(cents) && cents > 0 && cents <= fullCents;
    const noteValid = note.trim().length > 0;
    const canSubmit = amountValid && noteValid && !busy;

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4"
            data-testid="refund-modal"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md border border-zinc-800 bg-zinc-950 p-5"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-2 mb-1">
                    <Undo2 className="w-4 h-4 text-brand-red" />
                    <h3 className="text-[12px] tracking-[0.24em] uppercase text-white font-display font-bold">
                        Refund charge
                    </h3>
                </div>
                <p className="text-xs text-zinc-500 mb-4 truncate">
                    {item.description || "Charge"} · {fmtMoney(fullCents)} · {fmtDate(item.occurred_at)}
                </p>

                <label className="block text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1">
                    Refund amount
                </label>
                <div className="relative mb-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                    <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={(fullCents / 100).toFixed(2)}
                        value={amountStr}
                        onChange={(e) => setAmountStr(e.target.value)}
                        data-testid="refund-amount-input"
                        className="w-full bg-black border border-zinc-800 pl-6 pr-3 py-2.5 text-base text-white focus:outline-none focus:border-brand-red"
                    />
                </div>
                <div className="flex items-center justify-between mb-4">
                    <button
                        type="button"
                        onClick={() => setAmountStr((fullCents / 100).toFixed(2))}
                        data-testid="refund-full-amount-btn"
                        className="text-[10px] tracking-[0.16em] uppercase text-zinc-500 hover:text-white"
                    >
                        Full ({fmtMoney(fullCents)})
                    </button>
                    {!amountValid && (
                        <span className="text-[10px] text-red-400" data-testid="refund-amount-error">
                            Enter $0.01–{fmtMoney(fullCents)}
                        </span>
                    )}
                    {amountValid && cents < fullCents && (
                        <span className="text-[10px] text-sky-300">Partial refund</span>
                    )}
                </div>

                <label className="block text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1">
                    Reason (Stripe)
                </label>
                <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid="refund-reason-select"
                    className="w-full bg-black border border-zinc-800 px-3 py-2.5 text-sm text-white mb-4 focus:outline-none focus:border-brand-red"
                >
                    <option value="requested_by_customer">Requested by customer</option>
                    <option value="duplicate">Duplicate</option>
                    <option value="fraudulent">Fraudulent</option>
                </select>

                <label className="block text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1">
                    Note <span className="text-brand-red">*</span>
                </label>
                <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    placeholder="Why is this being refunded? (saved to the audit log)"
                    data-testid="refund-note-input"
                    className="w-full bg-black border border-zinc-800 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-brand-red mb-5"
                />

                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        data-testid="refund-cancel-btn"
                        className="text-[11px] tracking-[0.18em] uppercase text-zinc-400 hover:text-white px-3 py-2 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={!canSubmit}
                        onClick={() => onSubmit({ item, amountCents: cents, note, reason })}
                        data-testid="refund-submit-btn"
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-brand-red bg-brand-red text-white px-4 py-2 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                        Refund {amountValid ? fmtMoney(cents) : ""}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function PaymentHistoryPanel({ userId }) {
    const { user } = useAuth();
    const canRefund = user?.role === "owner";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [refundingId, setRefundingId] = useState(null);
    // Sachin 2026-03-06 — "I just refunded but I have no confirmation
    // on my end. Can you make sure if something gets refunded, its
    // confirmed and can't be refunded again?"
    // Track charge_ids we've already refunded THIS session so the
    // button hides immediately (no waiting for the Stripe webhook to
    // land). Combined with the server-side idempotency guard, this
    // makes double-refunds impossible via the UI.
    const [refundedLocally, setRefundedLocally] = useState(new Set());
    const [refundItem, setRefundItem] = useState(null);

    const load = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const r = await axios.get(
                `${API}/coach/clients/${userId}/payment-history?limit=50`,
                { withCredentials: true },
            );
            setItems(r.data?.items || []);
            setErr(null);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to load payment history");
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleRefund = (item) => setRefundItem(item);

    // amountCents: exact cents to refund. If it equals the full charge we send
    // no `amount_cents` so the server keeps its strong "already fully refunded"
    // guard; otherwise we send the partial amount. `note` is always required.
    const doRefund = async ({ item, amountCents, note, reason }) => {
        const label = item.description || "this charge";
        const isPartial = amountCents < (item.amount_cents || 0);
        setRefundingId(item.id);
        try {
            const r = await axios.post(
                `${API}/coach/accounting/refund`,
                {
                    stripe_charge_id: item.stripe_charge_id,
                    reason: reason || "requested_by_customer",
                    amount_cents: isPartial ? amountCents : undefined,
                    note: note.trim(),
                },
                { withCredentials: true },
            );
            // A partial refund may leave room to refund more, so only hide the
            // button permanently on a full refund.
            if (!isPartial) {
                setRefundedLocally((s) => new Set([...s, item.stripe_charge_id]));
            }
            const refundAmt = r.data?.amount_cents ? fmtMoney(r.data.amount_cents) : fmtMoney(amountCents);
            toast.success(`Refunded ${refundAmt}${isPartial ? " (partial)" : ""}`, {
                description: `Stripe confirmed refund_id: ${r.data?.refund_id?.slice(0, 12) || "—"}. Row updates on next refresh.`,
                duration: 8000,
            });
            setRefundItem(null);
            setTimeout(load, 2000);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const detailStr = typeof detail === "string" ? detail
                : (typeof detail === "object" ? (detail?.message || detail?.code) : "Check Stripe dashboard for details.");
            if (detailStr && detailStr.toLowerCase().includes("already refunded")) {
                setRefundedLocally((s) => new Set([...s, item.stripe_charge_id]));
                setRefundItem(null);
                toast.info("Already refunded", {
                    description: "This charge was refunded previously. The row will update on next refresh.",
                });
            } else {
                toast.error("Refund failed", { description: detailStr, duration: 10000 });
            }
        } finally {
            setRefundingId(null);
        }
    };

    const failedCount = items.filter((i) => i.status === "failed").length;

    return (
        <section
            data-testid="payment-history-panel"
            className="border border-zinc-800 bg-zinc-950/40 p-4 mt-6"
        >
            <header className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <h3 className="text-[11px] tracking-[0.28em] uppercase text-zinc-400 font-display font-bold">
                        Payment History
                    </h3>
                    {failedCount > 0 && (
                        <span
                            data-testid="payment-history-failed-count"
                            className="text-[9px] tracking-[0.18em] uppercase text-red-300 border border-red-700/60 bg-red-900/30 px-1.5 py-0.5 font-bold"
                        >
                            {failedCount} failed
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    data-testid="payment-history-refresh"
                    className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 hover:text-white transition-colors flex items-center gap-1 disabled:opacity-50"
                >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
                    Refresh
                </button>
            </header>

            {loading && items.length === 0 ? (
                <div className="text-xs text-zinc-500 py-4 text-center" data-testid="payment-history-loading">
                    Loading…
                </div>
            ) : err ? (
                <div className="text-xs text-red-300 py-3" data-testid="payment-history-error-banner">
                    {err}
                </div>
            ) : items.length === 0 ? (
                <div className="text-xs text-zinc-500 py-4 text-center" data-testid="payment-history-empty">
                    No payments on record yet.
                </div>
            ) : (
                <ul data-testid="payment-history-list">
                    {items.map((it) => (
                        <Row
                            key={it.id}
                            item={it}
                            onRefund={handleRefund}
                            refundingId={refundingId}
                            canRefund={canRefund}
                            locallyRefunded={refundedLocally.has(it.stripe_charge_id)}
                        />
                    ))}
                </ul>
            )}
            {refundItem && (
                <RefundModal
                    item={refundItem}
                    busy={refundingId === refundItem.id}
                    onClose={() => { if (refundingId !== refundItem.id) setRefundItem(null); }}
                    onSubmit={doRefund}
                />
            )}
        </section>
    );
}
